import { apiClient } from '../apiClient';
import { ApiResponse } from '../http/types';
import { getStoredToken } from '../http/httpClient';
import { ENV } from '../../config/env';
import {
  AlarmItem,
  AlarmDashboardResponse,
  AlarmDetailResponse,
  AlarmStatisticsSummary,
  DetailedStatisticsResponse,
  SiteAlarmsResponse,
  AlarmsByPathsResponse,
  VirtualTenantAlarmsResponse,
  AcknowledgePayload,
  HidePayload,
  DashboardParams,
  ListAlarmsParams,
  AlarmReportRequest,
  AlarmReportDownloadRequest,
  AlarmReportResponse,
} from './alarm.types';

const base = '/api/Alarm';

export function getAlarmDashboard(params: DashboardParams): Promise<ApiResponse<AlarmDashboardResponse>> {
  return apiClient.get<AlarmDashboardResponse>(`${base}/dashboard`, { params, headers: { Accept: 'text/plain' }} as any);
}

export function getAlarm(id: string | number): Promise<ApiResponse<AlarmDetailResponse>> {
  return apiClient.get<AlarmDetailResponse>(`${base}/${id}` as any);
}

export function listAlarms(params?: ListAlarmsParams): Promise<ApiResponse<AlarmItem[]>> {
  return apiClient.get<AlarmItem[]>(base, { params } as any);
}

export function acknowledgeAlarm(id: string | number, payload?: AcknowledgePayload): Promise<ApiResponse<void>> {
  return apiClient.post<void>(`${base}/${id}/acknowledge` as any, payload as any);
}

export function hideAlarm(id: string | number, payload?: HidePayload): Promise<ApiResponse<void>> {
  return apiClient.post<void>(`${base}/${id}/hide` as any, payload as any);
}

export function getAlarmsBySite(siteId: string | number, params?: ListAlarmsParams): Promise<ApiResponse<SiteAlarmsResponse>> {
  return apiClient.get<SiteAlarmsResponse>(`${base}/site/${siteId}` as any, { params } as any);
}

export function getAlarmStatistics(params?: any): Promise<ApiResponse<AlarmStatisticsSummary>> {
  return apiClient.get<AlarmStatisticsSummary>(`${base}/statistics` as any, { params } as any);
}

export function getAlarmStatisticsDetailed(params?: { startDate?: string; endDate?: string; topPathsLimit?: number; page?: number; pageSize?: number }): Promise<ApiResponse<DetailedStatisticsResponse>> {
  return apiClient.get<DetailedStatisticsResponse>(`${base}/statistics/detailed` as any, { params } as any);
}

export function getAlarmsBySiteByPaths(siteId: string | number, params?: ListAlarmsParams): Promise<ApiResponse<AlarmsByPathsResponse>> {
  return apiClient.get<AlarmsByPathsResponse>(`${base}/site/${siteId}/by-paths` as any, { params } as any);
}

export function getAlarmsByVirtualTenant(vtId: string | number, params?: ListAlarmsParams): Promise<ApiResponse<VirtualTenantAlarmsResponse>> {
  return apiClient.get<VirtualTenantAlarmsResponse>(`${base}/virtual-tenant/${vtId}` as any, { params } as any);
}

export function getAlarmReport(request: AlarmReportRequest): Promise<ApiResponse<AlarmReportResponse>> {
  return apiClient.post<AlarmReportResponse>(`${base}/report`, request);
}

type CsvCellValue = string | number | boolean | null | undefined;

function escapeCsvCell(value: CsvCellValue): string {
  if (value === null || value === undefined) {
    return '';
  }

  const text = String(value);
  const escaped = text.replace(/"/g, '""');
  if (/[,\r\n"]/.test(escaped)) {
    return `"${escaped}"`;
  }

  return escaped;
}

function reportToCsv(report: AlarmReportResponse): string {
  const header = [
    'Id',
    'Alarm Timestamp',
    'Alarm Message',
    'Alarm Path',
    'Alarm Path Id',
    'Priority',
    'Priority Name',
    'Alarm State Id',
    'Alarm State Name',
    'Acknowledged',
    'Site Name',
    'Site Registry Id',
  ];

  const lines: string[] = [header.join(',')];
  const alarms = report.alarms ?? [];

  alarms.forEach(alarm => {
    const row = [
      alarm.id,
      alarm.alarmTimestamp,
      alarm.alarmMessage,
      alarm.alarmPath,
      alarm.alarmPathId,
      alarm.priority,
      alarm.priorityName,
      alarm.alarmStateId,
      alarm.alarmStateName,
      alarm.acknowledged,
      alarm.siteName,
      alarm.siteRegistryId,
    ].map(escapeCsvCell);

    lines.push(row.join(','));
  });

  // Add UTF-8 BOM to help Excel detect encoding correctly.
  return `\uFEFF${lines.join('\r\n')}`;
}

function isJsonContentType(contentType: string | null): boolean {
  if (!contentType) {
    return false;
  }

  const normalized = contentType.toLowerCase();
  return normalized.includes('application/json') || normalized.includes('text/json') || normalized.includes('application/problem+json');
}

/**
 * Downloads the alarm report as a file blob.
 * Uses fetch directly to avoid Axios treating error responses as blobs.
 */
export async function downloadAlarmReport(
  request: AlarmReportDownloadRequest
): Promise<{ blob: Blob; filename: string }> {
  const HTTP_NOT_FOUND = 404;
  const HTTP_METHOD_NOT_ALLOWED = 405;

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = await getStoredToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const requestInit: RequestInit = {
    headers,
    method: 'POST',
    body: JSON.stringify(request),
  };

  const buildUrl = (path: string): string => {
    if (ENV.USE_DIRECT_API) {
      return new URL(path, ENV.API_BASE_URL).toString();
    }

    return path;
  };

  const primaryUrl = buildUrl(`${base}/report/download`);
  const fallbackUrl = buildUrl(`${base}/report`);

  let res = await fetch(primaryUrl, requestInit);
  if (!res.ok && (res.status === HTTP_NOT_FOUND || res.status === HTTP_METHOD_NOT_ALLOWED)) {
    res = await fetch(fallbackUrl, requestInit);
  }

  if (!res.ok) {
    let errorDetail = `${res.status} ${res.statusText}`;
    try {
      const text = await res.text();
      if (text) { errorDetail = text; }
    } catch {
      // ignore
    }
    throw new Error(`Download failed: ${errorDetail}`);
  }

  if (isJsonContentType(res.headers.get('content-type'))) {
    const report = await res.json() as AlarmReportResponse;
    const csv = reportToCsv(report);
    const csvBlob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const csvFilename = `alarm-report-${new Date().toISOString().slice(0, 10)}.csv`;
    return { blob: csvBlob, filename: csvFilename };
  }

  const downloadBlob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
  const downloadFilename = filenameMatch?.[1]?.replace(/['"]/g, '') ?? `alarm-report-${new Date().toISOString().slice(0, 10)}.csv`;

  return { blob: downloadBlob, filename: downloadFilename };
}
